package com.karawek.core.auth.stytch.client

import kotlinx.coroutines.await
import kotlin.time.Instant

private val nativeJSON: dynamic = js("JSON")

private val clientCache = mutableMapOf<String, dynamic>()
private fun client(token: String): dynamic = clientCache.getOrPut(token) {
    js("new (require('@stytch/vanilla-js').StytchUIClient)(token)")
}

private fun doSmsLoginOrCreate(c: dynamic, phone: String, expMins: Int): dynamic =
    c.otps.sms.loginOrCreate(phone, nativeJSON.parse("""{"expiration_minutes":$expMins}"""))

private fun doEmailLoginOrCreate(c: dynamic, email: String, expMins: Int): dynamic =
    c.otps.email.loginOrCreate(email, nativeJSON.parse("""{"expiration_minutes":$expMins}"""))

private fun doAuthenticate(c: dynamic, methodId: String, code: String, sessionMins: Int): dynamic =
    c.otps.authenticate(code, methodId, nativeJSON.parse("""{"session_duration_minutes":$sessionMins}"""))

internal actual suspend fun jsInitiateSmsOtp(
    publicToken: String,
    phoneE164: String,
    expirationMinutes: Int,
): InitiateOtpResponse {
    val r: dynamic = doSmsLoginOrCreate(client(publicToken), phoneE164, expirationMinutes)
        .unsafeCast<kotlin.js.Promise<dynamic>>().await()
    return InitiateOtpResponse(
        methodId = r.method_id.unsafeCast<String>(),
        requestId = r.request_id.unsafeCast<String>(),
        statusCode = r.status_code.unsafeCast<Int>(),
    )
}

internal actual suspend fun jsInitiateEmailOtp(
    publicToken: String,
    email: String,
    expirationMinutes: Int,
): InitiateOtpResponse {
    val r: dynamic = doEmailLoginOrCreate(client(publicToken), email, expirationMinutes)
        .unsafeCast<kotlin.js.Promise<dynamic>>().await()
    return InitiateOtpResponse(
        methodId = r.method_id.unsafeCast<String>(),
        requestId = r.request_id.unsafeCast<String>(),
        statusCode = r.status_code.unsafeCast<Int>(),
    )
}

internal actual suspend fun jsSignOut(publicToken: String) {
    client(publicToken).sessions.revoke()
        .unsafeCast<kotlin.js.Promise<dynamic>>().await()
}

internal actual suspend fun jsStartOAuth(
    publicToken: String,
    providerName: String,
    loginRedirectUrl: String,
    signupRedirectUrl: String,
) {
    val c = client(publicToken)
    val params = nativeJSON.parse("""{"login_redirect_url":"$loginRedirectUrl","signup_redirect_url":"$signupRedirectUrl"}""")
    val provider: dynamic = when (providerName) {
        "google" -> c.oauth.google
        "apple" -> c.oauth.apple
        "facebook" -> c.oauth.facebook
        else -> throw IllegalArgumentException("Unsupported OAuth provider: $providerName")
    }
    provider.start(params).unsafeCast<kotlin.js.Promise<dynamic>>().await()
}

internal actual suspend fun jsOAuthAuthenticate(
    publicToken: String,
    token: String,
    sessionDurationMinutes: Int,
): AuthenticationResponse {
    val c = client(publicToken)
    val params = nativeJSON.parse("""{"session_duration_minutes":$sessionDurationMinutes}""")
    val r: dynamic = c.oauth.authenticate(token, params)
        .unsafeCast<kotlin.js.Promise<dynamic>>().await()
    return AuthenticationResponse(
        userId = r.user.user_id.unsafeCast<String>(),
        sessionToken = r.session_token.unsafeCast<String>(),
        sessionJwt = r.session_jwt.unsafeCast<String>(),
        sessionId = r.session.session_id.unsafeCast<String>(),
        startedAt = Instant.parse(r.session.started_at.unsafeCast<String>()),
        expiresAt = Instant.parse(r.session.expires_at.unsafeCast<String>()),
        firstName = r.user.name.first_name as? String,
        middleName = r.user.name.middle_name as? String,
        lastName = r.user.name.last_name as? String,
        emailAddresses = (r.user.emails as Array<dynamic>).map { it.email.unsafeCast<String>() },
        phoneNumbers = (r.user.phone_numbers as Array<dynamic>).map { it.phone_number.unsafeCast<String>() },
        profilePictureUrl = null,
    )
}

internal actual fun jsReplaceHistoryState(url: String) {
    js("window.history.replaceState(null, '', url)")
}

internal actual suspend fun jsVerifyOtp(
    publicToken: String,
    methodId: String,
    otpCode: String,
    sessionDurationMinutes: Int,
): AuthenticationResponse {
    val r: dynamic = doAuthenticate(client(publicToken), methodId, otpCode, sessionDurationMinutes)
        .unsafeCast<kotlin.js.Promise<dynamic>>().await()
    return AuthenticationResponse(
        userId = r.user.user_id.unsafeCast<String>(),
        sessionToken = r.session_token.unsafeCast<String>(),
        sessionJwt = r.session_jwt.unsafeCast<String>(),
        sessionId = r.session.session_id.unsafeCast<String>(),
        startedAt = Instant.parse(r.session.started_at.unsafeCast<String>()),
        expiresAt = Instant.parse(r.session.expires_at.unsafeCast<String>()),
        firstName = r.user.name.first_name as? String,
        middleName = r.user.name.middle_name as? String,
        lastName = r.user.name.last_name as? String,
        emailAddresses = (r.user.emails as Array<dynamic>).map { it.email.unsafeCast<String>() },
        phoneNumbers = (r.user.phone_numbers as Array<dynamic>).map { it.phone_number.unsafeCast<String>() },
        profilePictureUrl = null,
    )
}

internal actual fun jsCurrentSessionJwt(publicToken: String): String? {
    val tokens: dynamic = client(publicToken).session.getTokens()
    return tokens?.session_jwt as? String
}

internal actual fun jsCurrentSessionToken(publicToken: String): String? {
    val tokens: dynamic = client(publicToken).session.getTokens()
    return tokens?.session_token as? String
}

internal actual suspend fun jsRenewSession(publicToken: String, durationMinutes: Int) {
    client(publicToken).session
        .authenticate(nativeJSON.parse("""{"session_duration_minutes":$durationMinutes}"""))
        .unsafeCast<kotlin.js.Promise<dynamic>>().await()
}

internal actual suspend fun jsRegisterPasskey(publicToken: String, domain: String) {
    client(publicToken).webauthn
        .register(nativeJSON.parse("""{"domain":"$domain"}"""))
        .unsafeCast<kotlin.js.Promise<dynamic>>().await()
}

internal actual suspend fun jsAuthenticateWithPasskey(
    publicToken: String,
    sessionDurationMinutes: Int,
): AuthenticationResponse {
    val r: dynamic = client(publicToken).webauthn
        .authenticate(nativeJSON.parse("""{"session_duration_minutes":$sessionDurationMinutes}"""))
        .unsafeCast<kotlin.js.Promise<dynamic>>().await()
    return AuthenticationResponse(
        userId = r.user.user_id.unsafeCast<String>(),
        sessionToken = r.session_token.unsafeCast<String>(),
        sessionJwt = r.session_jwt.unsafeCast<String>(),
        sessionId = r.session.session_id.unsafeCast<String>(),
        startedAt = Instant.parse(r.session.started_at.unsafeCast<String>()),
        expiresAt = Instant.parse(r.session.expires_at.unsafeCast<String>()),
        firstName = r.user.name.first_name as? String,
        middleName = r.user.name.middle_name as? String,
        lastName = r.user.name.last_name as? String,
        emailAddresses = (r.user.emails as Array<dynamic>).map { it.email.unsafeCast<String>() },
        phoneNumbers = (r.user.phone_numbers as Array<dynamic>).map { it.phone_number.unsafeCast<String>() },
        profilePictureUrl = null,
    )
}
